import axios from 'axios';
import { apiSlice } from 'api/apiSlice';
import { attachConnectivity } from 'features/connectivity';

// Separate axios instance with NO auth interceptor.
// createToken + verifyCaptcha fire before a session token exists, so 401/403
// from these must not redirect to /session-expired. The 2FA endpoints send a
// Bearer header explicitly with the freshly created onboarding token rather
// than relying on the global interceptor's `window.store` read.
const tokenAxios = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: {
        Accept: 'application/json',
    },
    maxContentLength: 1024 * 1024 * 20,
});

// Reported to the connection banner only; these are all POSTs, so nothing is held or retried.
attachConnectivity(tokenAxios);

// queryFn's `error` field is opaque to RTK Query — pass the axios error
// through verbatim so consumers (and getErrorMessage) keep seeing the
// `error.response.data.errorMessage` shape they had before sub-phase 1d.
const toQueryFnError = (error) => ({ error });

export const onboardingApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        createToken: builder.mutation({
            queryFn: async (data) => {
                try {
                    const response = await tokenAxios.post(
                        '/verification/self/onboarding/demo/jwt',
                        {
                            ...data,
                            authToken: process.env.REACT_APP_COMPANY_LOW_CODE_SDK_KEY,
                        }
                    );
                    return { data: response.data };
                } catch (error) {
                    return toQueryFnError(error);
                }
            },
        }),
        verifyCaptcha: builder.mutation({
            queryFn: async (captchaValue) => {
                try {
                    const response = await tokenAxios.post('/verification/self/captcha/verify', {
                        response: captchaValue,
                    });
                    if (!response.data.success) {
                        // Preserves the legacy `throw new Error('Verification Failed')`
                        // shape — OnboardingForm.container.js catches and calls
                        // setErrors({ captcha: 'Captcha verification failed...' }).
                        return { error: new Error('Verification Failed') };
                    }
                    return { data: response.data.success };
                } catch (error) {
                    return toQueryFnError(error);
                }
            },
        }),
        twoFactorAuth: builder.mutation({
            queryFn: async (token) => {
                try {
                    const response = await tokenAxios.get('/verification/self/two-fa', {
                        headers: { Authorization: token ? `Bearer ${token}` : '' },
                    });
                    return { data: response.data };
                } catch (error) {
                    return toQueryFnError(error);
                }
            },
        }),
        verifyTwoFactorAuth: builder.mutation({
            queryFn: async ({ code, token }) => {
                try {
                    const response = await tokenAxios.post(
                        '/verification/self/two-fa/confirm',
                        { code },
                        { headers: { Authorization: token ? `Bearer ${token}` : '' } }
                    );
                    return { data: response.data };
                } catch (error) {
                    return toQueryFnError(error);
                }
            },
        }),
    }),
});

export const {
    useCreateTokenMutation,
    useVerifyCaptchaMutation,
    useTwoFactorAuthMutation,
    useVerifyTwoFactorAuthMutation,
} = onboardingApi;
